"use client";

/**
 * The question, asked once in a browser before anything of the browser's is
 * recorded (#788 PR2; the user's ruling of 2026-10-01: asked at the start, so
 * onboarding is recorded from its first page by those who agree).
 *
 * What the law asks of it, and so what it is: a choice, not a notice — the
 * two answers side by side, the same size and weight, neither chosen
 * beforehand; saying no changes nothing about what they can do here; and
 * closing it without an answer is not a yes (it asks again next time). The
 * answer is posted (/api/consent), which sets the browser's cookie and, when
 * signed in, keeps it on the account.
 */
import { useEffect, useRef, useState } from "react";
import { t, type Lang } from "@/lib/i18n/index";
import { NavigationAnchor } from "./NavigationPending";

/** `onAnswer(null)`: put away without a stored answer — the post failed, so
 *  nothing is recorded and the question comes back on the next load. */
export function ConsentDialog({ lang, onAnswer }: { lang: Lang; onAnswer: (choice: "granted" | "denied" | null) => void }) {
  const s = t(lang);
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && !el.open && typeof el.showModal === "function") el.showModal();
  }, []);

  async function answer(choice: "granted" | "denied") {
    if (busy) return;
    setBusy(true);
    let stored = false;
    try {
      const res = await fetch("/api/consent", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ analytics: choice, where: "dialog" }),
      });
      stored = res.ok;
    } catch {
      /* not stored */
    }
    // Not stored: the cookie was not set, so the server takes nothing from
    // this browser either way — and this page does not start as if it did.
    if (ref.current?.open) ref.current.close();
    onAnswer(stored ? choice : null);
  }

  return (
    <dialog
      ref={ref}
      className="modal-card consent-dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-body"
      data-consent=""
      data-esc=""
    >
      <h2 id="consent-title">{s["consent.title"]}</h2>
      <p id="consent-body">{s["consent.body"]}</p>
      <p className="hint">{s["consent.change"]} <NavigationAnchor href="/privacy">{s["consent.more"]}</NavigationAnchor></p>
      <div className="consent-answers">
        <button type="button" className="btn" data-consent-answer="denied" disabled={busy} onClick={() => answer("denied")}>{s["consent.no"]}</button>
        <button type="button" className="btn" data-consent-answer="granted" disabled={busy} onClick={() => answer("granted")}>{s["consent.yes"]}</button>
      </div>
    </dialog>
  );
}
